import React, { useState, useEffect, useRef, useCallback } from 'react';
import APIService from '../services/api';
import { isAoAdmin } from './AoSociete';
import { confirmErpia } from './ConfirmDialog';
import { avecCle } from '../services/cleLecture';

const BASE = 'https://besoins-tat-production.up.railway.app';
const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

const STATUS = {
  draft:     { label: 'Brouillon', bg: 'var(--chip-bg)',              fg: 'var(--text-2)', icon: 'pencil' },
  sent:      { label: 'Envoyé',    bg: 'rgba(56,132,255,0.14)',       fg: '#3b82f6',       icon: 'send' },
  completed: { label: 'Signé',     bg: 'rgba(45,200,110,0.16)',       fg: '#16a34a',       icon: 'check-circle-fill' },
  declined:  { label: 'Refusé',    bg: 'rgba(226,35,26,0.12)',        fg: 'var(--brand)',  icon: 'x-circle' },
  voided:    { label: 'Annulé',    bg: 'var(--chip-bg)',              fg: 'var(--text-3)', icon: 'slash-circle' },
  cancelled: { label: 'Annulé',    bg: 'var(--chip-bg)',              fg: 'var(--text-3)', icon: 'slash-circle' },
  expired:   { label: 'Expiré',    bg: 'var(--chip-bg)',              fg: 'var(--text-3)', icon: 'hourglass-bottom' },
};

/**
 * Bloc « Signatures » de la fiche candidat — adossé à signd (clé API côté serveur).
 * Le navigateur ne voit que prepare_url (placement des zones en popup signd).
 */
/**
 * Enveloppe produite par la signature automatique des contrats.
 *
 * Faute de marqueur au contrat, on reconnaît d'abord ce que le serveur dit
 * (`auto_signe`, un mode ou une source), puis, à défaut, le nom du document.
 * Le repli sur le nom est volontairement étroit : masquer par erreur une
 * demande de signature réelle serait pire que d'en laisser passer une
 * automatique.
 */
const estAutoSignature = (env) => {
  if (env?.auto_signe === true || env?.auto === true) return true;
  const meta = `${env?.mode || ''} ${env?.source || ''} ${env?.origine || ''} ${env?.type || ''}`.toLowerCase();
  if (/auto[_ -]?sign|contrat_auto/.test(meta)) return true;
  const nom = `${env?.title || ''} ${env?.filename || ''}`;
  return /(^|[^a-z])(cdi|cdd)[_ -]/i.test(nom);
};

export default function SignaturesBlock({ candidateId, candidateEmail, besoinId, showNotification, cardBox, sectTitle }) {
  const isAdmin = isAoAdmin(APIService.getCurrentUser());
  const [list, setList] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actingId, setActingId] = useState(null);
  const [pendingFile, setPendingFile] = useState(null); // fichier en attente d'email (need_email)
  const [emailInput, setEmailInput] = useState('');
  const fileRef = useRef(null);
  const authH = { Authorization: `Bearer ${APIService.getToken()}` };
  const base = `${BASE}/candidates/${candidateId}/signatures`;

  const load = useCallback(async () => {
    try {
      const r = await fetch(base, { headers: authH });
      if (!r.ok) { setList([]); return; }
      const j = await r.json();
      setList(j.data || j.signatures || (Array.isArray(j) ? j : []));
    } catch { setList([]); }
  }, [base]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (candidateId != null) load(); }, [candidateId, load]);

  // Ouvre signd pour le placement des zones ; ferme + rafraîchit à l'envoi.
  const openDocsign = (prepareUrl) => {
    if (!prepareUrl) return;
    const win = window.open(prepareUrl, 'docsign', 'width=1050,height=850');
    const onMsg = (e) => {
      if (e.data?.source === 'docsign' && e.data.event === 'envelope.sent') {
        try { win?.close(); } catch { /* noop */ }
        window.removeEventListener('message', onMsg);
        showNotification?.('Document envoyé pour signature', 'success');
        load();
      }
    };
    window.addEventListener('message', onMsg);
  };

  const createEnvelope = async (file, email) => {
    if (!file) return;
    if (file.type && !/pdf/i.test(file.type)) { showNotification?.('Sélectionne un PDF.', 'error'); return; }
    if (file.size > 4 * 1024 * 1024) { showNotification?.('PDF trop lourd (max 4 Mo).', 'error'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', file.name.replace(/\.pdf$/i, ''));
      if (besoinId != null) fd.append('besoin_id', besoinId);
      if (email) fd.append('email', email);
      const r = await fetch(base, { method: 'POST', headers: authH, body: fd });
      const j = await r.json().catch(() => ({}));
      if (r.status === 400 && j.need_email) { setPendingFile(file); showNotification?.('Aucun email sur la fiche — précise l\'adresse du signataire.', 'error'); return; }
      // Refus métier (adresse invalide…) : message du serveur affiché tel quel
      // et panneau laissé ouvert pour corriger l'adresse.
      if (r.status === 400) { setPendingFile(file); showNotification?.(j.error || j.message || 'Envoi refusé.', 'error'); return; }
      if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
      setPendingFile(null); setEmailInput('');
      openDocsign(j.prepare_url || j.prepareUrl || j.envelope?.prepareUrl);
      load();
    } catch (e) { showNotification?.(`Échec : ${e.message}`, 'error'); } finally { setBusy(false); }
  };

  const pickFile = () => fileRef.current?.click();

  // Le serveur renvoie l'URL du viewer maison (jamais un lien d'app kDrive) :
  // on la rend absolue si besoin et on y joint la CLÉ DE LECTURE — un nouvel
  // onglet ne peut pas envoyer d'en-tête d'authentification, et le jeton de
  // session ne voyage plus dans une adresse (28.09).
  const urlSignee = (u) => {
    if (!u) return null;
    const abs = u.startsWith('/') ? `${BASE}${u}` : u;
    return avecCle(abs);
  };

  // Le document signé : la liste ne porte pas toujours son URL (le PDF signé
  // s'archive quelques secondes après la signature). On relit l'enveloppe
  // (même route que « Actualiser ») et on ouvre ce que le serveur donne.
  const ouvrirSigne = async (env) => {
    const id = env.envelope_id || env.envelopeId || env.id;
    setActingId(id);
    try {
      const r = await fetch(`${base}/${id}`, { headers: authH });
      const j = await r.json().catch(() => ({}));
      const d = j.data || j;
      const url = urlSignee(d.signed_document_url || d.signed_url || d.document_url
        || d.envelope?.signed_document_url || d.envelope?.signed_url || d.envelope?.document_url);
      if (url) { window.open(url, '_blank', 'noopener'); return; }
      showNotification?.('Le document sign\u00e9 est encore en cours d\u2019archivage \u2014 r\u00e9essayez dans quelques secondes.', 'error');
    } catch (e) { showNotification?.(`\u00c9chec : ${e.message}`, 'error'); } finally { setActingId(null); }
  };

  // LE DOCUMENT TEL QU'IL EST PARTI (28.09) — relu avec l'en-tête
  // d'authentification, puis ouvert ou téléchargé depuis la mémoire du
  // navigateur : AUCUN jeton dans l'adresse (un lien qui porte la session se
  // retrouve dans l'historique, et se transmet d'un copier-coller).
  const documentEnvoye = async (env, telecharger = false) => {
    const id = env.envelope_id || env.envelopeId || env.id;
    setActingId(id);
    // L'onglet s'ouvre AVANT l'attente : ouvert après, le navigateur le bloque.
    const onglet = telecharger ? null : window.open('', '_blank');
    try {
      const r = await fetch(`${base}/${encodeURIComponent(id)}/document`, { headers: authH });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      const url = URL.createObjectURL(await r.blob());
      if (telecharger) {
        const a = document.createElement('a');
        a.href = url; a.download = `${(env.title || 'document').replace(/[\\/:*?"<>|]/g, '_')}.pdf`;
        document.body.appendChild(a); a.click(); a.remove();
      } else if (onglet) {
        onglet.location.href = url;
      } else {
        window.open(url, '_blank');
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) {
      if (onglet) onglet.close();
      showNotification?.(e.message, 'error');
    } finally { setActingId(null); }
  };

  // Retirer un envoi TERMINÉ SANS SIGNATURE (annulé, refusé, expiré). Le
  // serveur refuse un envoi en cours ou signé, et dit pourquoi.
  const retirer = async (env) => {
    const id = env.envelope_id || env.envelopeId || env.id;
    if (!await confirmErpia({ message: `Retirer « ${env.title || 'ce document'} » de la liste des signatures ? L'envoi reste tracé dans l'historique du candidat.` })) return;
    setActingId(id);
    try {
      const r = await fetch(`${base}/${encodeURIComponent(id)}`, { method: 'DELETE', headers: authH });
      const j = await r.json().catch(() => ({}));
      if (!r.ok || j.success === false) throw new Error(j.error || `HTTP ${r.status}`);
      setList(prev => (prev || []).filter(x => (x.envelope_id || x.envelopeId || x.id) !== id));
      showNotification?.(j.message || 'Envoi retiré', 'success');
    } catch (e) { showNotification?.(e.message, 'error'); } finally { setActingId(null); }
  };

  const action = async (env, kind) => {
    const id = env.envelope_id || env.envelopeId || env.id;
    setActingId(id);
    try {
      if (kind === 'reprendre' || kind === 'refresh') {
        const r = await fetch(`${base}/${id}`, { headers: authH });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const j = await r.json(); const d = j.data || j;
        if (kind === 'reprendre') openDocsign(d.prepare_url || d.prepareUrl);
        else showNotification?.('Statut actualisé', 'success');
        load();
      } else {
        const r = await fetch(`${base}/${id}/${kind}`, { method: 'POST', headers: authH });
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        showNotification?.(kind === 'remind' ? 'Relance envoyée' : 'Enveloppe annulée', 'success');
        load();
      }
    } catch (e) { showNotification?.(`Échec : ${e.message}`, 'error'); } finally { setActingId(null); }
  };

  // Les contrats auto-signés sont produits et signés par le système : ils
  // n'appellent aucun geste, et trois lignes identiques masquent les vraies
  // demandes de signature en attente. Réservés à l'administration, qui les
  // pilote depuis le dossier AO. La section, elle, reste affichée pour tous —
  // « Faire signer un document » n'est pas un geste d'administrateur.
  const rows = (list || []).filter(env => isAdmin || !estAutoSignature(env));
  const pill = (st) => { const s = STATUS[st] || STATUS.draft; return { display: 'inline-flex', alignItems: 'center', gap: '5px', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '6px', background: s.bg, color: s.fg }; };
  const miniBtn = { fontFamily: 'inherit', fontSize: '11.5px', fontWeight: 600, padding: '4px 9px', borderRadius: '6px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)', color: 'var(--text-2)', cursor: 'pointer' };

  return (
    <div style={cardBox}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '9px', marginBottom: (rows.length ? 12 : 0) }}>
        <span style={{ ...sectTitle, marginBottom: 0 }}>Signatures</span>
        <span className="ats-mono" style={{ fontSize: '11px', color: 'var(--text-2)', background: 'var(--chip-bg)', padding: '1px 7px', borderRadius: '6px' }}>{list == null ? '…' : rows.length}</span>
        {/* L'email du signataire se confirme AVANT l'envoi : repris de la fiche
            s'il y est, sinon vide — et c'est cette adresse qui part à signd. */}
        <input ref={fileRef} type="file" accept="application/pdf" className="hidden" style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; e.target.value = ''; if (f) { setPendingFile(f); setEmailInput((candidateEmail || '').trim()); } }} />
        <button className="ats-btn ats-btn-ghost" onClick={pickFile} disabled={busy} style={{ marginLeft: 'auto', padding: '6px 11px', borderRadius: '7px', fontSize: '12.5px', fontWeight: 600, opacity: busy ? 0.6 : 1 }}>
          <BI icon={busy ? 'arrow-repeat' : 'vector-pen'} style={busy ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> Faire signer un document
        </button>
      </div>

      {pendingFile && (
        <div style={{ margin: '4px 0 12px', padding: '9px 11px', borderRadius: '8px', border: '1px solid var(--input-border)', background: 'var(--ghost-bg)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ fontSize: '12px', color: 'var(--text-2)', flex: 'none', maxWidth: '180px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={pendingFile.name}>Envoyer « {pendingFile.name} » à :</span>
            <input value={emailInput} onChange={e => setEmailInput(e.target.value)} type="email" autoFocus placeholder="prenom.nom@email.com"
              onKeyDown={e => { if (e.key === 'Enter' && /.+@.+\..+/.test(emailInput)) createEnvelope(pendingFile, emailInput.trim()); if (e.key === 'Escape') { setPendingFile(null); setEmailInput(''); } }}
              style={{ flex: 1, minWidth: 0, padding: '6px 9px', borderRadius: '6px', border: '1px solid var(--input-border)', background: 'var(--input-bg)', color: 'var(--text)', fontFamily: 'inherit', fontSize: '12.5px', outline: 'none' }} />
            <button style={{ ...miniBtn, border: 'none', background: 'var(--accent)', color: 'var(--accent-fg)' }} disabled={busy || !/.+@.+\..+/.test(emailInput)} onClick={() => createEnvelope(pendingFile, emailInput.trim())}>Envoyer</button>
            <button style={miniBtn} disabled={busy} onClick={() => { setPendingFile(null); setEmailInput(''); }}>Annuler</button>
          </div>
          <div style={{ marginTop: '5px', fontSize: '11px', color: 'var(--text-faint)' }}>
            {candidateEmail
              ? 'Adresse reprise de la fiche candidat — modifiable avant l\'envoi ; c\'est elle qui reçoit la demande de signature.'
              : 'Aucun email sur la fiche candidat — saisissez l\'adresse du signataire.'}
          </div>
        </div>
      )}

      {list == null ? (
        <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Chargement…</div>
      ) : rows.length === 0 ? (
        <div style={{ fontSize: '12.5px', color: 'var(--text-faint)' }}>Aucun document envoyé à signer.</div>
      ) : rows.map((env, i) => {
        const id = env.envelope_id || env.envelopeId || env.id;
        const st = env.status || 'draft';
        const acting = actingId === id;
        const signedUrl = urlSignee(env.signed_document_url || env.signed_url || env.document_url);
        return (
          <div key={id || i} style={{ padding: '11px 0', borderTop: i === 0 ? 'none' : '1px solid var(--hair)' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <BI icon="file-earmark-pdf" style={{ color: 'var(--brand)', flex: 'none' }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: '13.5px', fontWeight: 600, color: 'var(--text-strong)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{env.title || env.filename || 'Document'}</div>
                {(env.signer_email || env.email) && <div style={{ fontSize: '11.5px', color: 'var(--text-2)' }}>{env.signer_email || env.email}</div>}
              </div>
              <span style={pill(st)}><BI icon={(STATUS[st] || STATUS.draft).icon} /> {(STATUS[st] || STATUS.draft).label}</span>
            </div>
            <div style={{ display: 'flex', gap: '6px', marginTop: '8px', flexWrap: 'wrap' }}>
              {st === 'draft' && <button style={miniBtn} disabled={acting} onClick={() => action(env, 'reprendre')}><BI icon="pencil-square" /> Reprendre le placement</button>}
              {st === 'sent' && <button style={miniBtn} disabled={acting} onClick={() => action(env, 'remind')}><BI icon="bell" /> Relancer</button>}
              {(st === 'draft' || st === 'sent') && <button style={miniBtn} disabled={acting} onClick={() => action(env, 'void')}><BI icon="x-lg" /> Annuler</button>}
              {st === 'completed' && (signedUrl
                ? <a href={signedUrl} target="_blank" rel="noreferrer" style={{ ...miniBtn, textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '5px' }}><BI icon="download" /> Document signé</a>
                : <button style={miniBtn} disabled={acting} onClick={() => ouvrirSigne(env)}><BI icon="download" /> Document signé</button>)}
              {/* Le document envoyé, relisible tant qu'il n'est pas signé (le
                  signé a son propre bouton). Absent pour un envoi plus ancien
                  que la copie gardée à l'envoi. */}
              {env.document_envoye && st !== 'completed' && (
                <>
                  <button style={miniBtn} disabled={acting} onClick={() => documentEnvoye(env)}><BI icon="eye" /> Voir</button>
                  <button style={miniBtn} disabled={acting} onClick={() => documentEnvoye(env, true)}><BI icon="download" /> Télécharger</button>
                </>
              )}
              <button style={miniBtn} disabled={acting} onClick={() => action(env, 'refresh')}><BI icon={acting ? 'arrow-repeat' : 'arrow-clockwise'} style={acting ? { animation: 'ats-spin .7s linear infinite' } : undefined} /> Actualiser</button>
              {['voided', 'cancelled', 'declined', 'expired'].includes(st) && (
                <button style={{ ...miniBtn, marginLeft: 'auto' }} disabled={acting} onClick={() => retirer(env)}
                  title="Retirer cet envoi de la liste : il reste tracé dans l'historique du candidat"><BI icon="trash3" /> Retirer</button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
